import React, { useState, useRef, useMemo } from 'react';

import { Autocomplete, AutocompleteProps, AutocompleteItem } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import useAutoCompleteStaff from 'hooks/staff/useAutoCompleteStaff';

import SelectorButton from './SelectorButton';

export interface StaffAutoCompleteValue extends AutocompleteItem {
  staffId: string;
  label: string;
  isCoach?: boolean;
}

interface IStaffAutoCompleteProps
  extends Omit<
    AutocompleteProps,
    'data' | 'value' | 'onChange' | 'filter' | 'onItemSubmit' | 'rightSection'
  > {
  value: StaffAutoCompleteValue;
  onChange: (item: StaffAutoCompleteValue | null) => void;
  isCoach?: boolean;
  disinclude?: Array<string>;
}

const StaffAutoComplete = ({
  value,
  onChange,
  isCoach,
  disinclude = [],
  ...autocompleteProps
}: IStaffAutoCompleteProps): JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [search, setSearch] = useState(value.label);
  const [debouncedSearch] = useDebouncedValue(search, 250);

  const { data, isSuccess, isLoading } = useAutoCompleteStaff({
    search: debouncedSearch,
    isCoach: isCoach,
    loginEnabledFilter: true,
  });

  const handleSearchChange = (query: string): void => {
    setSearch(query);
    onChange(null);
  };
  const handleItemSubmit = (item: StaffAutoCompleteValue): void => {
    setSearch('');
    onChange(item);
  };
  const handleClearSearch = (): void => {
    setSearch('');
  };

  const RightSection = (
    <SelectorButton
      isLoading={isLoading}
      hasText={!!(value.label || search)}
      handleClick={() => handleSearchChange('')}
      handleFocus={() => inputRef?.current?.focus()}
    />
  );

  const filteredOptions = useMemo(() => {
    if (!isSuccess || !data) return [];

    return data.data
      .filter(staff => !disinclude.includes(staff.staffId))
      .map(staff => ({
        ...staff,
        value: staff.staffId,
        label: staff.fullName,
        isCoach: staff.isCoach,
      }));
  }, [isSuccess, data, disinclude]);

  return (
    <Autocomplete
      ref={inputRef}
      rightSection={RightSection}
      value={value.label || search}
      onChange={handleSearchChange}
      onItemSubmit={handleItemSubmit}
      onDropdownClose={handleClearSearch}
      filter={() => true}
      data={filteredOptions}
      limit={filteredOptions.length}
      maxDropdownHeight={200}
      {...autocompleteProps}
    />
  );
};

export default StaffAutoComplete;
